import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  TrendingUp, DollarSign, Sparkles, AlertTriangle, Star, Target,
  Wrench, Award, Calculator, Download, Loader2, ArrowRight, Crown,
  Info,
} from 'lucide-react';
import {
  Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { clientApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import IntelligenceDetailSheet from '@/components/client/IntelligenceDetailSheet';
import StrategicAnalysisDialog from '@/components/client/StrategicAnalysisDialog';
import StrategicRequestsHistory from '@/components/client/StrategicRequestsHistory';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtCurrency = (n, currency = 'USD') => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(Number(n));
  } catch { return `$${Math.round(Number(n)).toLocaleString(getUiLocale())}`; }
};

const fmtPct = (n) => (n === null || n === undefined ? '—' : `${Math.round(Number(n) * 10) / 10}%`);

const scoreColor = (s) => {
  if (s === null || s === undefined) return 'text-muted-foreground';
  if (s >= 80) return 'text-success';
  if (s >= 60) return 'text-warning';
  return 'text-destructive';
};
const scoreBg = (s) => {
  if (s === null || s === undefined) return 'bg-muted';
  if (s >= 80) return 'bg-success/15';
  if (s >= 60) return 'bg-warning/15';
  return 'bg-destructive/15';
};

function NumBadge({ n }) {
  return (
    <span
      className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0"
      aria-label={`Section ${n}`}
      data-testid={`cti-numbadge-${n}`}
    >
      {n}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, hint, accent = 'primary', testid, onClick, num, explainer }) {
  const accents = {
    primary: 'from-primary/15 to-primary/5 text-primary',
    success: 'from-success/15 to-success/5 text-success',
    warning: 'from-warning/15 to-warning/5 text-warning',
    destructive: 'from-destructive/15 to-destructive/5 text-destructive',
    info: 'from-info/15 to-info/5 text-info',
  };
  const interactive = typeof onClick === 'function';
  return (
    <Card
      className={`p-4 shadow-card hover:shadow-elevated transition-smooth relative ${interactive ? 'cursor-pointer focus:ring-2 focus:ring-primary focus:outline-none' : ''}`}
      data-testid={testid}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      {num != null && (
        <span className="absolute top-2 right-2"><NumBadge n={num} /></span>
      )}
      {/* Session L-E — Info icon + Tooltip surfaces the KPI formula clearly */}
      {explainer ? (
        <span className={`absolute ${num != null ? 'top-2 right-8' : 'top-2 right-2'}`}>
          <TooltipProvider delayDuration={150}>
            <UiTooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={explainer}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid={testid ? `${testid}-info` : undefined}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                className="max-w-xs text-xs leading-snug"
                data-testid={testid ? `${testid}-tooltip` : undefined}
              >
                {explainer}
              </TooltipContent>
            </UiTooltip>
          </TooltipProvider>
        </span>
      ) : null}
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${accents[accent]}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className={`text-xs uppercase tracking-wider text-muted-foreground font-medium leading-tight break-words ${(num != null || explainer) ? 'pr-10' : ''}`}>{label}</p>
          <p className="text-xl font-bold tabular-nums mt-0.5">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ClientTalentIntelligence() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [signals, setSignals] = useState({ climara: null, onboardingDash: null, gap: null, trainingUsers: null });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('Q2-2026');
  const [compare, setCompare] = useState('previousQ');
  const [departmentId, setDepartmentId] = useState('all');
  const [sheet, setSheet] = useState({ open: false, kind: null, payload: {} });
  const openSheet = useCallback((kind, payload = {}) => setSheet({ open: true, kind, payload }), []);
  const [strategicOpen, setStrategicOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Sprint A5 — pull real signals from sibling suites for the heatmap.
      // Each call is independent so a missing suite never blocks the page.
      const [overview, climara, onboardingDash, gap, trainingUsers] = await Promise.all([
        clientApi.intelligenceOverview(),
        clientApi.climaraConsolidated('en').catch(() => null),
        clientApi.onboardingDashboard().catch(() => null),
        clientApi.talentCompetencyGapAnalysis().catch(() => null),
        clientApi.ttListUsers().catch(() => ({ users: [] })),
      ]);
      setData(overview);
      setSignals({ climara, onboardingDash, gap, trainingUsers });
    } catch (e) {
      toast.error(t('toasts.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [t]);

  const handleExportCsv = useCallback(async () => {
    try {
      const params = (departmentId && departmentId !== 'all') ? { department_id: departmentId } : {};
      const res = await api.get('/client/intelligence/export.csv', {
        params, responseType: 'blob', timeout: 60000,
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'talent-intelligence.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    }
  }, [departmentId, t]);

  const handleExport = useCallback(async () => {
    try {
      const params = (departmentId && departmentId !== 'all') ? { department_id: departmentId } : {};
      const res = await api.get('/client/intelligence/export.pdf', {
        params, responseType: 'blob', timeout: 60000,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'talent-intelligence.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    }
  }, [departmentId, t]);

  useEffect(() => { load(); }, [load]);

  const employees = useMemo(() => {
    const all = data?.employees || [];
    if (departmentId === 'all') return all;
    return all.filter((e) => e.department?.id === departmentId);
  }, [data, departmentId]);

  const currency = data?.config?.currency || 'USD';
  const kpis = data?.kpis || {};

  // Performance pipeline buckets
  const buckets = useMemo(() => {
    const arr = [0, 0, 0, 0, 0]; // N1..N5
    for (const e of employees) {
      const s = e.value?.value_score ?? 0;
      if (s >= 80) arr[4]++;
      else if (s >= 65) arr[3]++;
      else if (s >= 50) arr[2]++;
      else if (s >= 35) arr[1]++;
      else arr[0]++;
    }
    return arr;
  }, [employees]);
  const totalEmp = employees.length || 1;

  // Top talent (top 5 by value_score)
  const topTalent = useMemo(() => {
    return [...employees]
      .sort((a, b) => (b.value?.value_score || 0) - (a.value?.value_score || 0))
      .slice(0, 5);
  }, [employees]);

  // Bonuses summary
  const bonusSummary = useMemo(() => {
    const eligible = employees.filter((e) => (e.value?.value_score || 0) >= 70).length;
    const notEligible = employees.filter((e) => (e.value?.value_score || 0) < 50).length;
    const pending = employees.length - eligible - notEligible;
    const economicImpact = employees
      .filter((e) => (e.value?.value_score || 0) >= 70)
      .reduce((s, e) => s + (e.cost?.components?.bonus || 0), 0);
    return { eligible, notEligible, pending, economicImpact };
  }, [employees]);

  // Average performance
  const avgPerf = useMemo(() => {
    if (employees.length === 0) return 0;
    return Math.round(
      employees.reduce((s, e) => s + (e.value?.value_score || 0), 0) / employees.length,
    );
  }, [employees]);

  const perfLevel = (
    avgPerf >= 80 ? t('clientTalentIntelligence.kpi.perfLevel.outstanding')
    : avgPerf >= 60 ? t('clientTalentIntelligence.kpi.perfLevel.meets')
    : avgPerf >= 40 ? t('clientTalentIntelligence.kpi.perfLevel.developing')
    : t('clientTalentIntelligence.kpi.perfLevel.critical')
  );

  // Scatter data — cost (real) on X, value_score (real) on Y. No synthetic
  // dollar conversion. The previous `valueDollars = score*1000` was removed.
  const scatterData = useMemo(() => employees.map((e) => ({
    name: e.user?.name?.split(' ').map((n) => n[0]).slice(0, 2).join('') || '?',
    fullName: e.user?.name,
    cost: e.cost?.loaded || 0,
    score: e.value?.value_score || 0,
    quadrant: e.quadrant,
  })), [employees]);

  // Department heatmap — Sprint A5 real signals only.
  // talent_value + risk come from intelligence overview (always real).
  // climate column joins to Climara `section3_by_department` by dept name.
  // competency column averages real gaps from talent-competency/gap-analysis.
  // training column averages real action completion % from talent-train users.
  // onboarding column joins to onboarding/dashboard by_department rollup.
  // When a signal is missing for a row, that cell shows "No data" (null).
  const heatmap = useMemo(() => {
    const rollup = data?.department_rollup || [];
    if (rollup.length === 0) return { rows: [], avg: null };

    // Build lookup maps from sibling-suite payloads.
    const climaraByName = {};
    for (const dep of signals.climara?.section3_by_department || []) {
      const dims = (dep.dimensions || []).filter((x) => x.avg != null);
      const avg = dims.length ? dims.reduce((s, x) => s + x.avg, 0) / dims.length : null;
      // Score in 0-5 → re-scaled to 0-100 to share the column tone.
      climaraByName[(dep.department_name || '').toLowerCase()] =
        avg != null ? Math.round(avg * 20) : null;
    }

    // Aggregate real competency gap per department (only users with actual_level set).
    const compByDeptName = {};
    if (signals.gap?.users && data?.employees) {
      const userToDeptName = {};
      for (const e of data.employees) {
        if (e.user?.id) userToDeptName[e.user.id] = e.department?.name;
      }
      const acc = {}; // deptName → { gapSum, n }
      for (const u of signals.gap.users) {
        const dept = userToDeptName[u.user?.id];
        if (!dept) continue;
        for (const c of u.competencies || []) {
          if (c.color === 'no_data' || c.actual_level == null) continue;
          const a = acc[dept] || (acc[dept] = { gapSum: 0, n: 0 });
          a.gapSum += (c.gap || 0);
          a.n += 1;
        }
      }
      for (const [dep, agg] of Object.entries(acc)) {
        // Gap → 0-100 inverse health (gap=0 ⇒ 100; gap=5 ⇒ 0)
        const meanGap = agg.gapSum / agg.n;
        compByDeptName[dep.toLowerCase()] = Math.round(Math.max(0, 100 - meanGap * 20));
      }
    }

    // Aggregate training completion per dept.
    const trainByDeptName = {};
    if (signals.trainingUsers?.users && data?.employees) {
      const userToDeptName = {};
      for (const e of data.employees) if (e.user?.id) userToDeptName[e.user.id] = e.department?.name;
      const acc = {};
      for (const row of signals.trainingUsers.users) {
        const dept = userToDeptName[row.user?.id];
        if (!dept) continue;
        const ac = row.action_counts || {};
        const total = (ac.total || 0);
        if (total === 0) continue;
        const a = acc[dept] || (acc[dept] = { done: 0, total: 0 });
        a.done += (ac.completed || 0);
        a.total += total;
      }
      for (const [dep, agg] of Object.entries(acc)) {
        trainByDeptName[dep.toLowerCase()] = Math.round(100 * agg.done / agg.total);
      }
    }

    // Onboarding load per dept.
    const onbByDeptName = {};
    for (const row of signals.onboardingDash?.by_department || []) {
      if (!row.department_name || row.department_name === '—') continue;
      const pct = row.stages > 0 ? Math.round(100 * (row.completed || 0) / row.stages) : null;
      onbByDeptName[row.department_name.toLowerCase()] = pct;
    }

    const rows = rollup.map((d) => {
      const key = (d.department?.name || '').toLowerCase();
      return {
        id: d.department?.id,
        name: d.department?.name,
        // Talent value 0-100 — real from value rollup
        talent_value: Math.round(d.avg_value || 0),
        // Real cost in currency
        cost: d.total_cost || null,
        // Real risk count (employees flagged at risk)
        risk: d.at_risk || 0,
        stars: d.stars || 0,
        // Real climate score (or null)
        climate: climaraByName[key] ?? null,
        // Real competency health (or null)
        competency: compByDeptName[key] ?? null,
        // Real training completion (or null)
        training: trainByDeptName[key] ?? null,
        // Real onboarding completion (or null)
        onboarding: onbByDeptName[key] ?? null,
      };
    });

    const avgOf = (key) => {
      const vals = rows.map((r) => r[key]).filter((v) => v != null);
      if (vals.length === 0) return null;
      return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
    };
    return {
      rows,
      avg: {
        talent_value: avgOf('talent_value'),
        climate: avgOf('climate'),
        competency: avgOf('competency'),
        training: avgOf('training'),
        onboarding: avgOf('onboarding'),
        risk: avgOf('risk'),
      },
    };
  }, [data, signals]);

  if (loading) {
    return (
      <>
        <Topbar title={t('clientTalentIntelligence.title')} subtitle={t('clientTalentIntelligence.subtitle')} />
        <div className="py-16 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      </>
    );
  }

  const PIPELINE_LABELS = [
    t('clientTalentIntelligence.pipeline.levels.n1'),
    t('clientTalentIntelligence.pipeline.levels.n2'),
    t('clientTalentIntelligence.pipeline.levels.n3'),
    t('clientTalentIntelligence.pipeline.levels.n4'),
    t('clientTalentIntelligence.pipeline.levels.n5'),
  ];

  return (
    <>
      <Topbar
        title={(
          <span className="inline-flex items-center gap-2">
            {t('clientTalentIntelligence.title')}
            <Badge className="bg-success/15 text-success border-success/30">
              {t('clientTalentIntelligence.activeBadge')}
            </Badge>
          </span>
        )}
        subtitle={t('clientTalentIntelligence.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" data-testid="cti-request-strategic"
                    onClick={() => setStrategicOpen(true)}>
              <Sparkles className="h-4 w-4 mr-1.5" /> {t('clientTalentIntelligence.actions.requestStrategic')}
            </Button>
            <Button size="sm" variant="outline" data-testid="cti-request-intervention"
                    onClick={() => openSheet('intervention')}>
              <Wrench className="h-4 w-4 mr-1.5" /> {t('clientTalentIntelligence.actions.requestIntervention')}
            </Button>
            <Button size="sm" variant="outline" data-testid="cti-export" onClick={handleExport}>
              <Download className="h-4 w-4 mr-1.5" /> {t('clientTalentIntelligence.actions.export')}
            </Button>
            <Button size="sm" variant="outline" data-testid="cti-export-csv" onClick={handleExportCsv}
                    title={t('clientTalentIntelligence.actions.exportCsvHint', { defaultValue: 'Tabular intelligence rollup as CSV' })}>
              <Download className="h-4 w-4 mr-1.5" /> {t('clientTalentIntelligence.actions.exportCsv', { defaultValue: 'Export CSV' })}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-5" data-testid="client-ti-page">
        {/* Filter bar — Sprint A5 honest disabled state for historical filters */}
        <Card className="p-4 shadow-card flex flex-wrap gap-3" data-testid="cti-filter-bar">
          <div className="flex items-center gap-2"
               title={t('clientTalentIntelligence.filters.disabledHint', {
                 defaultValue: 'Historical comparison will be available once multiple periods are configured.',
               })}>
            <span className="text-xs text-muted-foreground font-medium">{t('clientTalentIntelligence.filters.period')}</span>
            <Select value={period} onValueChange={setPeriod} disabled>
              <SelectTrigger className="w-32 h-8 opacity-60" data-testid="cti-period-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Q1-2026">Q1 2026</SelectItem>
                <SelectItem value="Q2-2026">Q2 2026</SelectItem>
                <SelectItem value="Q3-2026">Q3 2026</SelectItem>
                <SelectItem value="Q4-2026">Q4 2026</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2"
               title={t('clientTalentIntelligence.filters.disabledHint', {
                 defaultValue: 'Historical comparison will be available once multiple periods are configured.',
               })}>
            <span className="text-xs text-muted-foreground font-medium">{t('clientTalentIntelligence.filters.compareWith')}</span>
            <Select value={compare} onValueChange={setCompare} disabled>
              <SelectTrigger className="w-56 h-8 opacity-60" data-testid="cti-compare-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="previousQ">{t('clientTalentIntelligence.filters.previousQ')}</SelectItem>
                <SelectItem value="previousYear">{t('clientTalentIntelligence.filters.previousYear')}</SelectItem>
                <SelectItem value="ytd">{t('clientTalentIntelligence.filters.ytd')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-medium">{t('clientTalentIntelligence.filters.department')}</span>
            <Select value={departmentId} onValueChange={setDepartmentId}>
              <SelectTrigger className="w-44 h-8" data-testid="cti-dept-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('clientTalentIntelligence.filters.allDepartments')}</SelectItem>
                {(data?.department_rollup || []).map((d) => (
                  <SelectItem key={d.department?.id} value={d.department?.id}>
                    {d.department?.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* KPI row — Sprint A5 with explainability */}
        <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-4">
          <KpiCard icon={TrendingUp} label={t('clientTalentIntelligence.kpi.roi')}
                   value={fmtPct(kpis.positive_roi_pct)} accent="primary" testid="cti-kpi-roi"
                   num={1}
                   explainer={t('clientTalentIntelligence.kpi.expl.roi', { defaultValue: '% of employees with value_score ≥ loaded_cost / median_cost · source: intelligence/overview' })}
                   onClick={() => openSheet('roi')} />
          <KpiCard icon={DollarSign} label={t('clientTalentIntelligence.kpi.totalCost')}
                   value={fmtCurrency(kpis.total_cost, currency)} accent="info" testid="cti-kpi-total-cost"
                   num={2}
                   explainer={t('clientTalentIntelligence.kpi.expl.totalCost', { defaultValue: 'Sum of loaded_cost (salary + benefits + overhead) from user_compensations · source: intelligence/overview' })}
                   onClick={() => openSheet('cost')} />
          <KpiCard icon={Sparkles} label={t('clientTalentIntelligence.kpi.valueGenerated')}
                   value={kpis.avg_value != null && employees.length > 0
                     ? `${Math.round(kpis.avg_value)}/100`
                     : t('common.insufficientData', { defaultValue: 'Insufficient data' })}
                   hint={employees.length > 0 ? t('clientTalentIntelligence.kpi.valueHint', { defaultValue: 'Talent value score' }) : null}
                   accent="success" testid="cti-kpi-value"
                   num={3}
                   explainer={t('clientTalentIntelligence.kpi.expl.value', { defaultValue: 'Mean value_score (0-100) across all employees · source: intelligence/overview · No dollar conversion is performed' })}
                   onClick={() => openSheet('value')} />
          <KpiCard icon={Target} label={t('clientTalentIntelligence.kpi.performance')}
                   value={employees.length > 0 ? `${avgPerf}/100` : t('common.insufficientData', { defaultValue: 'Insufficient data' })}
                   hint={employees.length > 0 ? perfLevel : null}
                   accent="success" testid="cti-kpi-perf"
                   num={4}
                   explainer={t('clientTalentIntelligence.kpi.expl.performance', { defaultValue: 'Mean value_score for the selected scope · ≥80 outstanding · ≥60 meets · ≥40 developing · else critical' })}
                   onClick={() => openSheet('performance')} />
          <KpiCard icon={AlertTriangle} label={t('clientTalentIntelligence.kpi.atRisk')}
                   value={kpis.at_risk_count ?? 0} hint={t('clientTalentIntelligence.kpi.atRiskHint')}
                   accent="destructive" testid="cti-kpi-atrisk"
                   num={5}
                   explainer={t('clientTalentIntelligence.kpi.expl.atRisk', { defaultValue: 'Headcount with value_score below the at-risk threshold configured in intelligence/config' })}
                   onClick={() => openSheet('risk')} />
          <KpiCard icon={Crown} label={t('clientTalentIntelligence.kpi.topTalent')}
                   value={kpis.star_count ?? 0} hint={t('clientTalentIntelligence.kpi.topTalentHint')}
                   accent="warning" testid="cti-kpi-top"
                   num={6}
                   explainer={t('clientTalentIntelligence.kpi.expl.topTalent', { defaultValue: 'Headcount with value_score above the star threshold configured in intelligence/config' })}
                   onClick={() => openSheet('top')} />
        </div>

        {/* Row 1 — Heatmap / Scatter / Pipeline+QuickActions */}
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Heatmap */}
          <Card className="shadow-card overflow-hidden" data-testid="cti-heatmap">
            <div className="px-5 py-4 border-b border-border/60">
              <h3 className="font-semibold text-base inline-flex items-center gap-2">
                <NumBadge n={7} />
                {t('clientTalentIntelligence.heatmap.title')}
              </h3>
              <p className="text-xs text-muted-foreground">{t('clientTalentIntelligence.heatmap.subtitle')}</p>
            </div>
            {heatmap.rows.length === 0 ? (
              <div className="py-10 text-sm text-muted-foreground text-center">{t('clientTalentIntelligence.heatmap.empty')}</div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="text-xs" data-testid="cti-heatmap-table">
                  <TableHeader className="bg-muted/40">
                    <TableRow>
                      <TableHead className="text-xs">{t('clientTalentIntelligence.heatmap.th.department')}</TableHead>
                      <TableHead className="text-xs text-center" title={t('clientTalentIntelligence.heatmap.expl.talentValue', { defaultValue: 'Mean value_score from intelligence overview' })}>{t('clientTalentIntelligence.heatmap.th.performance', { defaultValue: 'Talent value' })}</TableHead>
                      <TableHead className="text-xs text-center" title={t('clientTalentIntelligence.heatmap.expl.climate', { defaultValue: 'Mean Climara dimension score × 20' })}>{t('clientTalentIntelligence.heatmap.th.climate')}</TableHead>
                      <TableHead className="text-xs text-center" title={t('clientTalentIntelligence.heatmap.expl.competency', { defaultValue: 'Inverse mean competency gap (100 - gap×20)' })}>{t('clientTalentIntelligence.heatmap.th.competency', { defaultValue: 'Competency' })}</TableHead>
                      <TableHead className="text-xs text-center" title={t('clientTalentIntelligence.heatmap.expl.training', { defaultValue: 'Completed / total training actions' })}>{t('clientTalentIntelligence.heatmap.th.training', { defaultValue: 'Training' })}</TableHead>
                      <TableHead className="text-xs text-center" title={t('clientTalentIntelligence.heatmap.expl.onboarding', { defaultValue: 'Completed / total onboarding stages' })}>{t('clientTalentIntelligence.heatmap.th.onboarding', { defaultValue: 'Onboarding' })}</TableHead>
                      <TableHead className="text-xs text-center" title={t('clientTalentIntelligence.heatmap.expl.risk', { defaultValue: 'At-risk headcount in this dept' })}>{t('clientTalentIntelligence.heatmap.th.risk')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {heatmap.rows.map((r) => (
                      <TableRow
                        key={r.id}
                        className="hover:bg-muted/30 cursor-pointer"
                        onClick={() => openSheet('dept', { dept: r })}
                        data-testid={`cti-heatmap-row-${r.id}`}
                      >
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell className={`text-center font-bold ${scoreColor(r.talent_value)} ${scoreBg(r.talent_value)}`}>{r.talent_value}</TableCell>
                        <TableCell className={`text-center font-bold ${r.climate == null ? 'text-muted-foreground' : `${scoreColor(r.climate)} ${scoreBg(r.climate)}`}`}>{r.climate == null ? t('common.noData', { defaultValue: 'No data' }) : r.climate}</TableCell>
                        <TableCell className={`text-center font-bold ${r.competency == null ? 'text-muted-foreground' : `${scoreColor(r.competency)} ${scoreBg(r.competency)}`}`}>{r.competency == null ? t('common.noData', { defaultValue: 'No data' }) : r.competency}</TableCell>
                        <TableCell className={`text-center font-bold ${r.training == null ? 'text-muted-foreground' : `${scoreColor(r.training)} ${scoreBg(r.training)}`}`}>{r.training == null ? t('common.noData', { defaultValue: 'No data' }) : r.training}</TableCell>
                        <TableCell className={`text-center font-bold ${r.onboarding == null ? 'text-muted-foreground' : `${scoreColor(r.onboarding)} ${scoreBg(r.onboarding)}`}`}>{r.onboarding == null ? t('common.noData', { defaultValue: 'No data' }) : r.onboarding}</TableCell>
                        <TableCell className={`text-center font-bold ${r.risk > 0 ? 'text-destructive' : 'text-success'}`}>{r.risk}</TableCell>
                      </TableRow>
                    ))}
                    {heatmap.avg && (
                      <TableRow className="bg-muted/60 font-bold">
                        <TableCell>{t('clientTalentIntelligence.heatmap.average')}</TableCell>
                        <TableCell className="text-center">{heatmap.avg.talent_value ?? '—'}</TableCell>
                        <TableCell className="text-center">{heatmap.avg.climate ?? '—'}</TableCell>
                        <TableCell className="text-center">{heatmap.avg.competency ?? '—'}</TableCell>
                        <TableCell className="text-center">{heatmap.avg.training ?? '—'}</TableCell>
                        <TableCell className="text-center">{heatmap.avg.onboarding ?? '—'}</TableCell>
                        <TableCell className="text-center">{heatmap.avg.risk ?? '—'}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>

          {/* Scatter */}
          <Card className="shadow-card p-4" data-testid="cti-scatter">
            <h3 className="font-semibold text-base inline-flex items-center gap-2">
              <NumBadge n={8} />
              {t('clientTalentIntelligence.scatter.title')}
            </h3>
            <p className="text-xs text-muted-foreground mb-2">{t('clientTalentIntelligence.scatter.subtitle')}</p>
            {scatterData.length === 0 ? (
              <div className="py-10 text-sm text-muted-foreground text-center">{t('clientTalentIntelligence.scatter.empty')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <ScatterChart margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis type="number" dataKey="cost" name={t('clientTalentIntelligence.scatter.x')} tick={{ fontSize: 10 }} />
                  <YAxis type="number" dataKey="score" name={t('clientTalentIntelligence.scatter.y')} domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v, n) => [v, n]} labelFormatter={() => ''} />
                  <ReferenceLine y={60} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                  <ReferenceLine x={Math.max(...scatterData.map((d) => d.cost)) / 2 || 50000} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                  <Scatter
                    data={scatterData}
                    fill="hsl(var(--primary))"
                    cursor="pointer"
                    onClick={(p) => {
                      const idx = scatterData.indexOf(p?.payload);
                      const emp = employees[idx];
                      if (emp) openSheet('employee', { employee: emp });
                    }}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            )}
            <div className="grid grid-cols-2 gap-1.5 text-xs text-muted-foreground mt-2">
              <span>↗ {t('clientTalentIntelligence.scatter.q1')}</span>
              <span>↖ {t('clientTalentIntelligence.scatter.q2')}</span>
              <span>↘ {t('clientTalentIntelligence.scatter.q4')}</span>
              <span>↙ {t('clientTalentIntelligence.scatter.q3')}</span>
            </div>
          </Card>

          {/* Pipeline + quick actions */}
          <div className="space-y-5">
            <Card className="shadow-card p-4" data-testid="cti-pipeline">
              <h3 className="font-semibold text-base mb-3 inline-flex items-center gap-2">
                <NumBadge n={9} />
                {t('clientTalentIntelligence.pipeline.title')}
              </h3>
              <div className="space-y-2.5">
                {[4, 3, 2, 1, 0].map((idx) => {
                  const count = buckets[idx];
                  const pct = Math.round((count / totalEmp) * 100);
                  return (
                    <button
                      key={idx}
                      type="button"
                      className="block w-full text-left focus:outline-none focus:ring-2 focus:ring-primary rounded"
                      onClick={() => openSheet('pipeline', { level: idx })}
                      data-testid={`cti-pipeline-level-${idx}`}
                    >
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium">{PIPELINE_LABELS[idx]}</span>
                        <span className="tabular-nums text-muted-foreground">{count} ({pct}%)</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className={
                            idx >= 3 ? 'bg-success' :
                            idx === 2 ? 'bg-info' :
                            idx === 1 ? 'bg-warning' :
                            'bg-destructive'
                          }
                          style={{ width: `${pct}%`, height: '100%' }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
            <Card className="shadow-card p-4">
              <h3 className="font-semibold text-base mb-3">{t('clientTalentIntelligence.quickActions.title')}</h3>
              <div className="space-y-1.5">
                {[
                  { icon: Target,     key: 'improvementPlans', kind: 'improvementPlans' },
                  { icon: Wrench,     key: 'requestTalentFix', kind: 'intervention' },
                  { icon: Award,      key: 'recognize',        kind: 'recognition' },
                  { icon: Calculator, key: 'simulateBonuses',  kind: 'bonus' },
                ].map(({ icon: Icon, key, kind }) => (
                  <Button
                    key={key}
                    variant="ghost"
                    className="w-full justify-start gap-2 h-8 text-xs"
                    onClick={() => openSheet(kind)}
                    data-testid={`cti-qa-${key}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {t(`clientTalentIntelligence.quickActions.${key}`)}
                  </Button>
                ))}
                <a href="/client/employees" className="block text-xs text-primary hover:underline pt-2">
                  {t('clientTalentIntelligence.quickActions.viewAllCollabs')}
                </a>
              </div>
            </Card>
          </div>
        </div>

        {/* Row 2 — Alerts / Top talent / Bonuses */}
        <div className="grid lg:grid-cols-3 gap-5">
          <Card
            className="shadow-card p-4 cursor-pointer hover:shadow-elevated transition-smooth"
            data-testid="cti-alerts"
            role="button"
            tabIndex={0}
            onClick={() => openSheet('alerts')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSheet('alerts'); } }}
          >
            <h3 className="font-semibold text-base">{t('clientTalentIntelligence.alerts.title')}</h3>
            <p className="text-xs text-muted-foreground mb-3">{t('clientTalentIntelligence.alerts.subtitle')}</p>
            <div className="py-6 text-sm text-muted-foreground text-center">
              {t('clientTalentIntelligence.alerts.empty')}
            </div>
            <span className="block text-xs text-primary hover:underline">
              {t('clientTalentIntelligence.alerts.viewAll')}
            </span>
          </Card>

          <Card className="shadow-card overflow-hidden" data-testid="cti-top-talent">
            <div className="px-5 py-4 border-b border-border/60">
              <h3 className="font-semibold text-base">{t('clientTalentIntelligence.topTalent.title')}</h3>
            </div>
            {topTalent.length === 0 ? (
              <div className="py-10 text-sm text-muted-foreground text-center">{t('clientTalentIntelligence.topTalent.empty')}</div>
            ) : (
              <Table className="text-xs">
                <TableHeader className="bg-muted/40">
                  <TableRow>
                    <TableHead className="text-xs w-8">{t('clientTalentIntelligence.topTalent.th.rank')}</TableHead>
                    <TableHead className="text-xs">{t('clientTalentIntelligence.topTalent.th.collab')}</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">{t('clientTalentIntelligence.topTalent.th.position')}</TableHead>
                    <TableHead className="text-xs text-center">{t('clientTalentIntelligence.topTalent.th.score')}</TableHead>
                    <TableHead className="text-xs text-center">{t('clientTalentIntelligence.topTalent.th.potential')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topTalent.map((e, i) => {
                    const s = e.value?.value_score || 0;
                    const potKey = s >= 80 ? 'very_high' : s >= 65 ? 'high' : 'medium';
                    const initials = (e.user?.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('');
                    return (
                      <TableRow key={e.user?.id}>
                        <TableCell className="font-bold tabular-nums">{i + 1}</TableCell>
                        <TableCell className="flex items-center gap-2">
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary font-semibold text-xs">
                            {initials}
                          </span>
                          <span className="font-medium truncate">{e.user?.name}</span>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground truncate">{e.user?.position || '—'}</TableCell>
                        <TableCell className={`text-center font-bold ${scoreColor(s)}`}>{Math.round(s)}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="text-xs">{t(`clientTalentIntelligence.topTalent.potential.${potKey}`)}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            <a href="/client/employees" className="block text-xs text-primary hover:underline px-4 py-2 border-t border-border/60">
              {t('clientTalentIntelligence.topTalent.viewAll')}
            </a>
          </Card>

          <Card className="shadow-card p-4" data-testid="cti-bonuses">
            <h3 className="font-semibold text-base mb-2">{t('clientTalentIntelligence.bonuses.title')}</h3>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={[
                    { name: t('clientTalentIntelligence.bonuses.eligible'), value: bonusSummary.eligible },
                    { name: t('clientTalentIntelligence.bonuses.notEligible'), value: bonusSummary.notEligible },
                    { name: t('clientTalentIntelligence.bonuses.pending'), value: bonusSummary.pending },
                  ]}
                  cx="50%" cy="50%" innerRadius={40} outerRadius={70}
                  dataKey="value" paddingAngle={2}
                >
                  <Cell fill="hsl(var(--success))" />
                  <Cell fill="hsl(var(--destructive))" />
                  <Cell fill="hsl(var(--muted))" />
                </Pie>
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="text-center mt-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('clientTalentIntelligence.bonuses.economicImpact')}</p>
              <p className="text-lg font-bold tabular-nums">{fmtCurrency(bonusSummary.economicImpact, currency)}</p>
            </div>
            <a href="/client/employees" className="block text-xs text-primary hover:underline mt-3 text-center">
              {t('clientTalentIntelligence.bonuses.viewDetail')}
            </a>
          </Card>
        </div>

        {/* Ecosystem connections */}
        <Card className="shadow-card p-5" data-testid="cti-ecosystem">
          <h3 className="font-semibold text-base">{t('clientTalentIntelligence.ecosystem.title')}</h3>
          <p className="text-xs text-muted-foreground mb-4">{t('clientTalentIntelligence.ecosystem.subtitle')}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
            {[
              { code: 'talentmap', impact: 'high' },
              { code: 'climara', impact: 'high' },
              { code: 'talentheart', impact: 'medium' },
              { code: 'talenttrain', impact: 'high' },
              { code: 'talentfix', impact: 'medium' },
              { code: 'talentcompetency', impact: 'high' },
            ].map(({ code, impact }) => (
              <button
                key={code}
                type="button"
                className="rounded-lg border border-border bg-muted/20 p-3 space-y-2 text-left hover:shadow-md hover:border-primary/40 transition-smooth focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid={`cti-ecosystem-${code}`}
                onClick={() => openSheet('suite', { suite: code })}
              >
                <div className="flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5 text-primary" />
                  <span className="font-semibold text-xs">{code}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-snug">
                  {t(`clientTalentIntelligence.ecosystem.${code}`)}
                </p>
                <Badge
                  variant="outline"
                  className={`text-xs ${impact === 'high' ? 'border-success/40 text-success' : 'border-warning/40 text-warning'}`}
                >
                  {t(`clientTalentIntelligence.ecosystem.impact${impact === 'high' ? 'High' : 'Medium'}`)}
                </Badge>
              </button>
            ))}
            {/* 7th card — Strategic AI (Sesión J-E Level A) */}
            <div
              className="rounded-lg border-2 border-primary/40 bg-gradient-to-br from-primary/10 to-primary/5 p-3 space-y-2 text-left"
              data-testid="cti-ecosystem-iastrategic"
            >
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <span className="font-semibold text-xs text-primary">
                  {t('clientTalentIntelligence.ecosystem.iaStrategic')}
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-snug">
                {t('clientTalentIntelligence.ecosystem.iaStrategicDesc')}
              </p>
              <button
                type="button"
                onClick={() => setStrategicOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
                data-testid="cti-ecosystem-iastrategic-cta"
              >
                {t('clientTalentIntelligence.ecosystem.iaStrategicCta')} <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setStrategicOpen(true)}
            className="inline-flex items-center gap-1 mt-4 text-xs text-primary hover:underline"
            data-testid="cti-ecosystem-view-all"
          >
            {t('clientTalentIntelligence.ecosystem.viewAll')} <ArrowRight className="h-3 w-3" />
          </button>
        </Card>

        <StrategicRequestsHistory refresh={strategicOpen} />
      </div>

      <IntelligenceDetailSheet
        open={sheet.open}
        onOpenChange={(v) => setSheet((s) => ({ ...s, open: v }))}
        kind={sheet.kind}
        payload={sheet.payload}
        data={data}
        currency={currency}
      />
      <StrategicAnalysisDialog
        open={strategicOpen}
        onOpenChange={setStrategicOpen}
      />
    </>
  );
}
